import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { runInAction } from 'mobx';
import debounce from 'lodash/debounce';
import { Content } from '../styles';
import { isEmpty } from '../../../../helpers/common';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import DeleteModal from '../../../../component/modals/deleteModal';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  BREAKPOINTS,
  COMMENT_TYPE,
  DEBOUNCE_DELAYS,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  STATUS_TYPE,
  TASK_PRIORITY
} from '../../../../global/constants';
import Modal from '../../../../component/modals/modal';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  createNewComment,
  updateBulkStatus,
  updateTaskDetails,
  archiveBulkTasks,
  dispatchTaskSuccess
} from '../../../../services/taskServices';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { TaskGroupInterface } from '../../../../interfaces/TaskInterface';
import { captureException } from '../../../../services/logService';
import TaskItem from '../taskItem';
import TaskList from './taskList';
import TaskConfirmModal, { TaskConfirmModalHandle } from './taskConfirmModal';
import { TASK_ANALYTICS } from '../../../../global/analyticsConstants';
import { TaskModel } from '../../../../mobx/models/task';
import { dispatchTaskUpdates, onUpdateStatus, checkSubtaskStatus } from '../../../../services/taskActionService';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import CreateTaskModalV2 from '../../../../component/modals/createTaskModal';
import { defaultDisplayFilters } from '../../../../mobx/common';
import BulkActionControls from '../../../../component/bulkActionControls';

const noop = () => {};

const DEFAULT_TASK_STATE = {
  filteredTasks: [],
  otherTasks: { tasks: [], completedTasks: [] }
};

interface Props {
  loadData: () => void;
  propsLoading: boolean;
  isTemplateProject?: boolean;
  hiddenTask?: number;
  isView?: boolean;
  showProjectName?: boolean;
  isCreateView?: boolean;
}

const TasksPage: React.FC<Props> = (props) => {
  const { loadData, isTemplateProject, hiddenTask = 0, isView, showProjectName = false, isCreateView = false } = props;

  // States
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteModalOpen, setSelectedDeleteModalOpen] = useState(false);
  const [showCompletedTask, setShowCompletedTask] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedItem, setSelectedItem] = useState<TaskModel>();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<{ taskId: string; groupId: string }[]>([]);
  const hoveredTask = useRef<{ taskId: string; groupId: string }>({ groupId: '', taskId: '' });

  const [selectedTasksFromCompleted, setSelectedTasksFromCompleted] = useState(false);
  const [isHoverSelect, setHoverSelect] = useState(selectedTasks?.length === 0);
  const [openSubTask, setOpenSubtask] = useState<{ id: string; showCompleted: boolean }>({
    id: '',
    showCompleted: false
  });
  const [collapseIds, setCollapseIds] = useState<string[]>([]);
  const [maxVisibleLabels, setMaxVisibleLabels] = useState(window.innerWidth > BREAKPOINTS.desktop_small ? 4 : 3);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [sourceContainerId, setSourceContainerId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Modal ref for task confirmation
  const taskConfirmModalRef = useRef<TaskConfirmModalHandle>(null);

  //Mobx store
  const { workspaceStore, projectStore, projectTemplateStore, viewStore, taskStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { currentProject: projectDetails } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const { currentView, createViewInput } = viewStore;
  const { tasks } = taskStore;

  const { filteredProjectTemplateTasks } = currentProjectTemplate || {};
  const { taskFilterType: projectTaskFilter, filteredProjectTasks } = projectDetails || {};
  const taskFilter = isView
    ? createViewInput?.id
      ? createViewInput?.displayFilters || defaultDisplayFilters
      : currentView?.displayFilters
    : projectTaskFilter;
  const { groupingStatus } = taskFilter || {};

  const { filteredTasks: filteredTasksList, otherTasks: otherTask } =
    (isTemplateProject ? filteredProjectTemplateTasks : isView ? viewStore.viewTasks : filteredProjectTasks) ||
    DEFAULT_TASK_STATE;
  const currentProject = isTemplateProject ? currentProjectTemplate : projectDetails;
  const filteredTasksListRef = useRef(filteredTasksList);
  const currentProjectRef = useRef(currentProject);

  useEffect(() => {
    filteredTasksListRef.current = filteredTasksList;
  }, [filteredTasksList]);

  useEffect(() => {
    currentProjectRef.current = currentProject;
  }, [currentProject]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    rootStore.taskStore.createTaskInput.clear();
  }, []);

  useEffect(() => {
    setSelectedTasks([]);
    setSelectedTasksFromCompleted(false);
    hoveredTask.current = { groupId: '', taskId: '' };
  }, [currentProject]);

  useEffect(() => {
    return () => {
      taskStore.clearCurrentAssignBoxTaskId();
    };
  }, [taskStore]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateLabelLimit = useCallback(
    debounce(() => {
      setMaxVisibleLabels(window.innerWidth > BREAKPOINTS.desktop_small ? 4 : 3);
    }, DEBOUNCE_DELAYS.RESIZE),
    []
  );

  useEffect(() => {
    setMaxVisibleLabels(window.innerWidth > BREAKPOINTS.desktop_small ? 4 : 3);

    window.addEventListener('resize', updateLabelLimit);
    return () => {
      window.removeEventListener('resize', updateLabelLimit);
      updateLabelLimit.cancel();
    };
  }, [updateLabelLimit]);

  // for open confirmation modal
  const handleOpenConfirmationModal = useCallback(
    (task: TaskModel, newStatus: StatusListInterface, previousStatus: StatusListInterface) => {
      taskConfirmModalRef.current?.open(task, newStatus, previousStatus);
    },
    []
  );

  // for close delete modal
  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  // for open selected delete modal
  const openSelectedDeleteModal = useCallback(() => {
    setSelectedDeleteModalOpen(true);
  }, []);

  // for close selected delete modal
  const closeSelectedDeleteModal = useCallback(() => {
    setSelectedDeleteModalOpen(false);
  }, []);

  // handle collapse click
  const handleCollapseClick = useCallback((id: string) => {
    const groupId = String(id);
    setCollapseIds((prev) => (prev.includes(groupId) ? prev.filter((x) => x !== groupId) : [...prev, groupId]));
  }, []);

  const onChangeStatus = useCallback(
    (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => {
      if (isTemplateProject) {
        if (currentProjectTemplate) {
          const updatedTaskList = currentProjectTemplate.tasks.map((item) => {
            if (item?.id === task.id) {
              item.setStatus(status['_id'], false, currentProjectTemplate);
            }
            return item;
          });
          rootStore.projectTemplateStore.currentProjectTemplate?.setTasks(updatedTaskList || []);
        }
      } else {
        const isSubtaskRemaining = checkSubtaskStatus(task);
        if (status.Type === STATUS_TYPE.COMPLETED && !isSubtaskRemaining) {
          handleOpenConfirmationModal(task, status, previousStatus);
          return;
        }
        onUpdateStatus(task, status, previousStatus, false);
      }
    },
    [currentProjectTemplate, handleOpenConfirmationModal, isTemplateProject]
  );

  //Handle checkbox click when project status is disabled
  const onClickTaskCheckBox = useCallback(
    (task: TaskModel) => {
      const status = !task.status;
      if (isTemplateProject) {
        if (currentProjectTemplate) {
          task.setStatus(status, false, currentProjectTemplate);
        }
      } else {
        const isSubtaskRemaining = checkSubtaskStatus(task);
        if (status && !isSubtaskRemaining) {
          // Show confirmation modal when checking task with uncompleted subtasks
          taskConfirmModalRef.current?.open(task);
          return;
        }
        task.setStatus(status, false);
        dispatchTaskUpdates(task, status);
      }
    },
    [currentProjectTemplate, isTemplateProject]
  );

  // logic for delete task
  const deleteTask = useCallback(
    async (task: TaskModel) => {
      try {
        setDeleteLoading(true);
        if (isTemplateProject) {
          const filteredTasks = currentProjectTemplate?.tasks?.filter((x) => x?.id !== task?.id) || [];
          rootStore.projectTemplateStore.currentProjectTemplate?.setTasks(filteredTasks);
          setDeleteModalOpen(false);
          return;
        }
        const payloadTask = { isArchived: true, projectId: task?.projectId };
        const response = await updateTaskDetails(task?.id, payloadTask);
        rootStore.taskStore.removeTasks([task?.id]);
        setDeleteModalOpen(false);
        if (response) {
          loadData && loadData();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setDeleteLoading(false);
      }
    },
    [loadData, currentProjectTemplate, isTemplateProject]
  );

  // Handle shortcut key 'C' to open the task creation modal with pre-filled default values.
  useHotkeys('c', async () => {
    setTimeout(() => {
      setOpen(true);
      rootStore.taskStore.createTaskInput.setProject(currentProject?.id || '');
    }, 100);
  });

  //logic for find container from task id
  const findContainer = useCallback((id: string): string | undefined => {
    const filteredTasks = filteredTasksListRef.current;
    if (!filteredTasks) return undefined;
    if (filteredTasks.find((group) => String(group.id) === id)) {
      return id;
    }
    const group = filteredTasks.find((group) => group.tasks?.some((t) => t.id === id));
    return group?.id != null ? String(group.id) : undefined;
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeId = String(event.active.id);
      setActiveTaskId(activeId);
      const container = findContainer(activeId);
      setSourceContainerId(container || null);
    },
    [findContainer]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      const filteredTasks = filteredTasksListRef.current;
      if (!over || !filteredTasks) return;

      if (
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING ||
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER
      ) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);

      const activeContainer = findContainer(activeId);
      const overContainer = findContainer(overId);

      if (!activeContainer || !overContainer || activeContainer === overContainer) return;

      const activeGroup = filteredTasks.find((g) => String(g.id) === activeContainer);
      const overGroup = filteredTasks.find((g) => String(g.id) === overContainer);
      if (!activeGroup || !overGroup) return;

      const project = currentProjectRef.current;
      const draggedTask = project?.tasks?.find((t) => t.id === activeId);
      if (!draggedTask) return;

      const overItems = overGroup.tasks || [];
      const overIndex = overItems.findIndex((t) => t.id === overId);
      const newIndex = overId === overContainer ? overItems.length : overIndex >= 0 ? overIndex : overItems.length;

      activeGroup.tasks = (activeGroup.tasks || []).filter((t) => t.id !== activeId);
      const insertTasks = [...(overGroup.tasks || [])].filter((t) => t.id !== activeId);
      insertTasks.splice(newIndex, 0, draggedTask);
      overGroup.tasks = insertTasks;
    },
    [findContainer, taskFilter?.groupingStatus]
  );

  //logic for drag end
  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTaskId(null);
      setSourceContainerId(null);

      const filteredTasks = filteredTasksListRef.current;
      const project = currentProjectRef.current;
      if (isView || !over || !filteredTasks) return;
      if (
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING ||
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER
      ) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const overContainer = findContainer(overId);
      if (!overContainer) return;

      const draggedTask = project?.tasks?.find((task) => task.id === activeId);
      if (!draggedTask) return;

      const sourceGroupId = sourceContainerId || overContainer;
      const destinationGroupId = overContainer;

      // Same-group reorder
      if (sourceGroupId === destinationGroupId) {
        if (!isTemplateProject && taskFilter?.orderByStatus !== SHOW_TASK_BY_ORDER.CUSTOM) return;

        const sourceGroup = filteredTasks.find((g) => String(g.id) === sourceGroupId);
        if (!sourceGroup) return;

        const groupTasks = sourceGroup.tasks || [];
        const activeIndex = groupTasks.findIndex((t) => t.id === activeId);
        const overIdx = groupTasks.findIndex((t) => t.id === overId);
        if (activeIndex === -1 || activeIndex === overIdx) return;

        let updatedTaskIndex: number;
        if (overIdx === 0) {
          const nextTaskIndex = groupTasks[0]?.index;
          updatedTaskIndex = nextTaskIndex ? nextTaskIndex - 50 : draggedTask.createdOn.getTime();
        } else if (overIdx === groupTasks.length - 1) {
          const previousTaskIndex = groupTasks[groupTasks.length - 1]?.index;
          updatedTaskIndex = previousTaskIndex ? previousTaskIndex + 50 : draggedTask.createdOn.getTime();
        } else {
          if (overIdx > activeIndex) {
            const previousTaskIndex: number = groupTasks[overIdx]?.index || 0;
            const nextTaskIndex: number = groupTasks[overIdx + 1]?.index || 0;
            updatedTaskIndex = (previousTaskIndex + nextTaskIndex) / 2;
          } else {
            const previousTaskIndex: number = groupTasks[overIdx - 1]?.index || 0;
            const nextTaskIndex: number = groupTasks[overIdx]?.index || 0;
            updatedTaskIndex = (previousTaskIndex + nextTaskIndex) / 2;
          }
        }
        draggedTask.setIndex(updatedTaskIndex);
        sourceGroup.tasks = arrayMove(groupTasks, activeIndex, overIdx);

        if (isTemplateProject && currentProjectTemplate) {
          const newTasksOrder = [...currentProjectTemplate.tasks].sort((a, b) => a.index - b.index);
          currentProjectTemplate.setTasks(newTasksOrder);
          currentProjectTemplate.setUpdated(true);
        } else {
          const payloadTask = { index: updatedTaskIndex, projectId: project?.id };
          await updateTaskDetails(activeId, payloadTask);
        }
        return;
      }

      // Cross-group drag — task already moved into destination by handleDragOver
      const destinationGroup = filteredTasks.find((g) => String(g.id) === destinationGroupId);
      if (!destinationGroup || !project) return;

      const destinationTasks = (destinationGroup.tasks || []).filter((t) => t.id !== activeId);
      const finalIndex = over.data.current?.sortable?.index ?? 0;

      // Calculate custom order index for cross-group drag
      const getCustomOrderIndex = () => {
        if (!isTemplateProject && taskFilter?.orderByStatus !== SHOW_TASK_BY_ORDER.CUSTOM) return undefined;
        if (destinationTasks.length === 0 || finalIndex > destinationTasks.length - 1) {
          return destinationTasks.length === 0
            ? draggedTask.createdOn.getTime()
            : (destinationTasks[destinationTasks.length - 1]?.index || 0) + 50;
        } else if (finalIndex === 0) {
          const nextTaskIndex = destinationTasks[0]?.index;
          return nextTaskIndex ? nextTaskIndex - 50 : draggedTask.createdOn.getTime();
        } else {
          const previousTaskIndex = destinationTasks[finalIndex - 1]?.index || 0;
          const nextTaskIndex = destinationTasks[finalIndex]?.index || 0;
          return (previousTaskIndex + nextTaskIndex) / 2;
        }
      };

      let payload: {
        index?: number;
        groupId?: string;
        statusId?: string;
        milestoneId?: string;
        priority?: number;
      } = {};

      if (
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS ||
        (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT && project?.statusEnable) ||
        (isTemplateProject && project?.statusEnable)
      ) {
        const newTaskIndex = getCustomOrderIndex();
        if (newTaskIndex !== undefined) {
          draggedTask.setIndex(newTaskIndex);
          payload.index = newTaskIndex;
        }
        if (isTemplateProject && currentProjectTemplate) {
          draggedTask.setStatus(destinationGroupId, false, currentProjectTemplate);
        } else {
          draggedTask.setStatus(destinationGroupId);
        }
        const currentStatus = project?.statusData?.find((x) => x['_id'] === destinationGroupId);
        const previousStatus = project?.statusData?.find((x) => x['_id'] === sourceGroupId);
        if (!isTemplateProject) {
          createNewComment(activeId, {
            Type: COMMENT_TYPE.STATUS_ID_CHANGE,
            PreviousGroup: previousStatus?.Name,
            Group: currentStatus?.Name,
            Priority: currentStatus?.Type
          });
        }
        payload.statusId = destinationGroupId;
      } else if (
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP ||
        (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT && project?.isGroupEnabled)
      ) {
        const newTaskIndex = getCustomOrderIndex();
        if (newTaskIndex !== undefined) {
          draggedTask.setIndex(newTaskIndex);
          payload.index = newTaskIndex;
        }
        draggedTask.setGroup(destinationGroupId);
        const taskGroups = draggedTask.project?.taskGroups;
        const previousGroup = taskGroups?.find((x) => x?.id === sourceGroupId);
        const updatedGroup = taskGroups?.find((x) => x?.id === destinationGroupId);
        if (!isTemplateProject) {
          createNewComment(activeId, {
            Type: COMMENT_TYPE.CHANGE_GROUP,
            PreviousGroup: previousGroup?.name,
            Group: updatedGroup?.name
          });
        }
        payload.groupId = destinationGroupId;
      } else if (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE) {
        draggedTask.setMilestone(destinationGroupId);
        const milestoneList = draggedTask?.project?.milestones || [];
        const milestone = milestoneList?.find((x) => x?.id === destinationGroupId);
        if (!isTemplateProject) {
          createNewComment(activeId, {
            Type: COMMENT_TYPE.SET_MILESTONE,
            Group: milestone?.milestoneName
          });
        }
        payload = { milestoneId: destinationGroupId };
      } else if (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY) {
        draggedTask.setPriority(Number(destinationGroupId));
        if (!isTemplateProject) {
          createNewComment(activeId, {
            Type: COMMENT_TYPE.CHANGE_PRIORITY,
            PreviousPriority: Number(sourceGroupId) === 5 ? TASK_PRIORITY.NO_PRIORITY : Number(sourceGroupId),
            Priority: Number(destinationGroupId) === 5 ? TASK_PRIORITY.NO_PRIORITY : Number(destinationGroupId)
          });
        }
        payload = {
          priority: draggedTask.priority === 5 ? TASK_PRIORITY.NO_PRIORITY : Number(destinationGroupId)
        };
      }

      if (isTemplateProject && currentProjectTemplate) {
        const newTasksOrder = [...currentProjectTemplate.tasks].sort((a, b) => a.index - b.index);
        currentProjectTemplate.setTasks(newTasksOrder);
        currentProjectTemplate.setUpdated(true);
      } else {
        const payloadTask = { ...(payload || {}), projectId: project?.id };
        const resultUpdate = await updateTaskDetails(activeId, payloadTask);
        if (resultUpdate) {
          trackAnalyticActivity(TASK_ANALYTICS.GROUP_CHANGED);
        }
      }
    },
    [isView, taskFilter, isTemplateProject, currentProjectTemplate, findContainer, sourceContainerId]
  );

  // toggle completed task
  const toggleCompletedTask = useCallback((value: boolean, id: string) => {
    if (!value) {
      setSelectedId('');
    }
    setSelectedId(id);
    setShowCompletedTask(value);
  }, []);

  // for select initial task
  const selectInitialTask = useCallback(() => {
    // Select the first task of the first group if nothing is selected or hovered.

    if (isEmpty(hoveredTask?.current?.taskId) && selectedTasks?.length === 0 && filteredTasksList) {
      const firstGroup = filteredTasksList[0];
      if (firstGroup && firstGroup?.tasks && firstGroup?.tasks?.length > 0) {
        setSelectedTasks([
          {
            groupId: groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING ? 'no_grouping' : `${firstGroup?.id || ''}`,
            taskId: firstGroup?.tasks[0]?.id
          }
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stale closure safe: selectedTasks?.length is guard condition only, no-op if non-zero
  }, [filteredTasksList, groupingStatus]);

  useEffect(() => {
    setHoverSelect(selectedTasks?.length === 0);
  }, [selectedTasks?.length]);

  // logic for find next selectable task
  const findNextSelectableTask = useCallback(
    (currentGroupId: string, currentTaskId: string, direction: string) => {
      const tasksToSearch = selectedTasksFromCompleted ? 'completedTasks' : 'tasks';
      const allGroups = [...(filteredTasksList || [])];
      const currentGroupIndex =
        groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING
          ? 0
          : allGroups?.findIndex((group) => group?.id === currentGroupId);
      const currentTasks: TaskModel[] = allGroups[currentGroupIndex]?.[tasksToSearch] || [];
      const currentTaskIndex: number = currentTasks?.findIndex((task) => task?.id === currentTaskId);

      if (direction === 'down') {
        if (currentTaskIndex + 1 < currentTasks?.length) {
          return { nextGroupId: currentGroupId, nextTaskId: currentTasks[currentTaskIndex + 1]?.id };
        }
      } else if (direction === 'up') {
        if (currentTaskIndex > 0) {
          return { nextGroupId: currentGroupId, nextTaskId: currentTasks[currentTaskIndex - 1]?.id };
        }
      }
      return { nextGroupId: null, nextTaskId: null };
    },
    [filteredTasksList, groupingStatus, selectedTasksFromCompleted]
  );

  // function for expand selection
  const expandSelection = useCallback(
    (direction: string) => {
      setSelectedTasks((selectedTasksState) => {
        let startPoint = hoveredTask.current;
        if (isEmpty(startPoint.taskId) && selectedTasksState.length > 0) {
          startPoint = selectedTasksState[selectedTasksState?.length - 1];
        }
        if (isEmpty(startPoint.taskId) && selectedTasksState?.length === 0) {
          selectInitialTask();
          return selectedTasksState;
        }

        if (!isEmpty(startPoint?.taskId) && selectedTasksState?.length === 0) {
          setHoverSelect(false);
          hoveredTask.current = { groupId: '', taskId: '' };
          return [
            {
              groupId: groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING ? 'no_grouping' : startPoint?.groupId,
              taskId: startPoint?.taskId
            }
          ];
        }
        let currentPoint = startPoint;

        let foundNewTask = false;

        // Create a working copy of selectedTasks to avoid mutating state directly
        const updatedSelectedTasks = [...selectedTasksState];

        for (let i = 0; i < updatedSelectedTasks?.length + 1; i++) {
          // Limit to the number of tasks + 1 to prevent infinite loops
          const next = findNextSelectableTask(currentPoint?.groupId, currentPoint?.taskId, direction);
          if (isEmpty(next?.nextGroupId) || isEmpty(next?.nextTaskId)) {
            // If no next task is found, reset to the first task of the same group
            // eslint-disable-next-line no-loop-func
            const firstTaskIdInGroup = filteredTasksList?.find((group) => group?.id === currentPoint?.groupId)
              ?.tasks?.[0]?.id;
            currentPoint = { groupId: currentPoint?.groupId, taskId: firstTaskIdInGroup || '' };
            break;
          }
          currentPoint = { groupId: next?.nextGroupId || '', taskId: next?.nextTaskId || '' };

          // Check if the new task is already selected or not
          const index = updatedSelectedTasks?.findIndex(
            // eslint-disable-next-line no-loop-func
            (task) => task?.groupId === currentPoint?.groupId && task?.taskId === currentPoint?.taskId
          );
          if (index === -1) {
            // If not found in the selected list, select it
            updatedSelectedTasks?.push(currentPoint);
            foundNewTask = true;
            break;
          } else if (i > 0) {
            continue;
          }
        }

        if (!foundNewTask && updatedSelectedTasks?.length > 0) {
          if (direction === 'down') {
            updatedSelectedTasks.shift(); // Remove the first element if navigating down
          } else {
            updatedSelectedTasks.pop(); // Remove the last element if navigating up
          }
        }
        setHoverSelect(updatedSelectedTasks?.length === 0);
        if (updatedSelectedTasks?.length === 0) {
          setSelectedTasksFromCompleted(false);
        }
        return updatedSelectedTasks;
      });
    },
    [filteredTasksList, groupingStatus, findNextSelectableTask, selectInitialTask]
  );

  //  Handle 'Shift+Down' shortcut to expand task selection downward and update the border radius for selected tasks.
  useHotkeys('shift+down', async () => {
    if (isView) return;
    const selectedItems = document.querySelectorAll<HTMLElement>('.selected-task');
    selectedItems.forEach(function (item) {
      item.style.borderRadius = '40px';
    });
    expandSelection('down');
  });

  // Handle 'Shift+Up' shortcut to expand task selection upward and update the border radius for selected tasks.
  useHotkeys('shift+up', async () => {
    if (isView) return;
    const selectedItems = document.querySelectorAll<HTMLElement>('.selected-task');
    selectedItems.forEach(function (item) {
      item.style.borderRadius = '40px';
    });
    expandSelection('up');
  });

  const handleHoverTask = useCallback(
    (groupId: string, taskId: string, isCompleted?: boolean) => {
      if (isHoverSelect) {
        setSelectedTasksFromCompleted(!!isCompleted);
        hoveredTask.current = { groupId, taskId };
      }
    },
    [isHoverSelect]
  );

  // for check is selected
  const isSelected = useCallback(
    (groupId: string, taskId: string) => {
      return selectedTasks?.some(
        (st: { groupId: string; taskId: string }) => st?.groupId === groupId && st?.taskId === taskId
      );
    },
    [selectedTasks]
  );

  const resetSelectedItemStyle = useCallback(() => {
    const selectedItems = document.querySelectorAll<HTMLElement>('.selected-task');
    selectedItems.forEach(function (item) {
      item.style.borderRadius = '40px';
    });
  }, []);

  // function for remove selected task
  const onRemoveSelectedTasks = useCallback(() => {
    resetSelectedItemStyle();
    setSelectedTasks([]);
    setHoverSelect(true);
    setSelectedTasksFromCompleted(false);
  }, [resetSelectedItemStyle]);

  // Esc clears selection unless a modal is open
  useHotkeys('esc', () => {
    if (selectedTasks.length === 0 || document.querySelector('[role="dialog"]')) return;
    onRemoveSelectedTasks();
  });

  // for delete selected task
  const onDeleteSelectedTasks = useCallback(
    async (tasks: { taskId: string; groupId: string }[]) => {
      try {
        setDeleteLoading(true);
        const taskIdsToArchive = tasks.map((task) => task.taskId);
        if (!currentProject?.id) return;
        const payload = {
          projectId: currentProject?.id,
          taskIds: taskIdsToArchive
        };
        const result = await archiveBulkTasks(payload);

        if (result) {
          dispatchTaskSuccess('Tasks archived successfully!');
          rootStore.taskStore.removeTasks(taskIdsToArchive);
          setSelectedTasks([]);
          loadData && loadData();
          setSelectedDeleteModalOpen(false);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setDeleteLoading(false);
      }
    },
    [currentProject?.id, loadData]
  );

  // Memoized callbacks for delete operations
  const handleDeleteSelected = useCallback(() => {
    if (selectedItem) deleteTask(selectedItem);
  }, [selectedItem, deleteTask]);

  const handleBulkDelete = useCallback(() => {
    onDeleteSelectedTasks(selectedTasks);
  }, [onDeleteSelectedTasks, selectedTasks]);

  const bulkDeleteDescription = useMemo(() => {
    const hasSubtasks = selectedTasks.some((selectedTask) => {
      const taskObj = tasks.find((task) => task.id === selectedTask.taskId);
      return taskObj?.subTasks && taskObj.subTasks.length > 0;
    });
    return hasSubtasks
      ? 'Are you sure you want to delete the selected tasks? Related sub-tasks will be deleted as well.'
      : 'Are you sure you want to delete this selected tasks?';
  }, [selectedTasks, tasks]);

  // Function to dynamically update border-radius for selected tasks based on their position
  const updateBorderRadius = useCallback(() => {
    const selectedItems = document.querySelectorAll<HTMLElement>('.selected-task');
    const numSelectedItems = selectedItems.length;

    selectedItems.forEach(function (item, index) {
      if (numSelectedItems === 1) {
        item.style.borderRadius = '40px';
      } else if (index === 0) {
        item.style.borderRadius = '18px 18px 0 0';
      } else if (index === numSelectedItems - 1) {
        item.style.borderRadius = '0 0 18px 18px';
      } else {
        item.style.borderRadius = '0';
      }
    });
  }, []);

  // Use useEffect with proper cleanup
  useEffect(() => {
    if (selectedTasks.length > 0) updateBorderRadius();
  }, [selectedTasks.length, updateBorderRadius]);

  // logic for bulk update group
  const onBulkUpdateGroup = useCallback(
    async (
      group: TaskGroupInterface,
      tasks: {
        taskId: string;
        groupId: string;
      }[]
    ) => {
      let promises: Promise<boolean>[] = [];
      tasks.forEach(async (task) => {
        const payloadTask = { groupId: group?.id, projectId: currentProject?.id };
        promises.push(updateTaskDetails(task?.taskId, payloadTask));
      });
      await Promise.all(promises);
      resetSelectedItemStyle();
      setSelectedTasks([]);
      if (loadData) loadData();
    },
    [currentProject?.id, loadData, resetSelectedItemStyle]
  );

  // logic for bulk update status
  const onBulkUpdateStatus = useCallback(
    async (
      status: StatusListInterface,
      previousStatus: StatusListInterface,
      tasks: { taskId: string; groupId: string }[]
    ) => {
      const taskIds = tasks.map((task) => task?.taskId);
      runInAction(() => {
        tasks.forEach((task) => {
          const taskDetails = rootStore.taskStore.tasks.find((x) => x?.id === task?.taskId);
          taskDetails?.setStatus(status?.['_id']);
        });
      });
      const comment = {
        Type: COMMENT_TYPE.STATUS_ID_CHANGE,
        Group: status?.Name,
        PreviousGroup: previousStatus?.Name,
        Priority: status?.Type
      };
      const payload = {
        projectId: currentProject?.id,
        taskIds,
        statusId: status?.['_id'],
        status: status?.['_id'] === previousStatus?.['_id'],
        comment
      };
      await updateBulkStatus(payload);
      resetSelectedItemStyle();
      setSelectedTasks([]);
      if (loadData) loadData();
    },
    [currentProject?.id, loadData, resetSelectedItemStyle]
  );

  // for show more options
  const handleShowOptions = useCallback(() => {
    rootStore.taskStore?.setIsTaskFilterOpen(true);
  }, []);

  //handle subtask show trigger
  const handleSubtaskOpen = useCallback(
    (id: string) => {
      let payload = {
        id: '',
        showCompleted: false
      };
      if (openSubTask?.id === id) {
        setOpenSubtask(payload);
      } else {
        setOpenSubtask({ ...payload, id: id });
      }
    },
    [openSubTask?.id]
  );

  //handle show completed subtask
  const handleShowCompletedSubTask = useCallback((id: string, isShow: boolean) => {
    let payload = {
      id: id,
      showCompleted: isShow
    };
    setOpenSubtask(payload);
  }, []);

  // Create memoized subtask data for each task
  const getSubtaskData = useCallback(
    (task: TaskModel) => {
      // Split is memoized per task on the model; here we only pick what to show.
      const activeSubtasks = task?.activeSubTasks || [];
      const completedSubtasks = task?.completedSubTasks || [];

      const showCompleted = openSubTask?.id === task?.id && openSubTask?.showCompleted;
      const visibleSubtasks = showCompleted ? [...activeSubtasks, ...completedSubtasks] : activeSubtasks;

      return { visibleSubtasks, completedSubtasks, hasCompleted: completedSubtasks.length > 0 };
    },
    [openSubTask?.id, openSubTask?.showCompleted]
  );

  const activeTask = useMemo(() => {
    if (!activeTaskId) return null;
    return currentProject?.tasks?.find((t) => t.id === activeTaskId) || null;
  }, [activeTaskId, currentProject?.tasks]);

  const modalActions = useMemo(() => ({ setOpen, setDeleteModalOpen, setSelectedItem }), []);

  // Filtered task list component

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={onDragEnd}>
        <Content isHide={groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING} isCreateView={isCreateView}>
          <TaskList
            filteredTasksList={filteredTasksList}
            otherTask={otherTask}
            taskFilter={taskFilter}
            hiddenTask={hiddenTask}
            handleShowOptions={handleShowOptions}
            collapseIds={collapseIds}
            handleCollapseClick={handleCollapseClick}
            loadData={loadData}
            currentWorkspace={currentWorkspace}
            isTemplateProject={isTemplateProject}
            currentProject={currentProject}
            isView={isView}
            showProjectName={showProjectName}
            selectedId={selectedId}
            showCompletedTask={showCompletedTask}
            openSubTask={openSubTask}
            isSelected={isSelected}
            handleHoverTask={handleHoverTask}
            onClickTaskCheckBox={onClickTaskCheckBox}
            onChangeStatus={onChangeStatus}
            handleSubtaskOpen={handleSubtaskOpen}
            handleShowCompletedSubTask={handleShowCompletedSubTask}
            toggleCompletedTask={toggleCompletedTask}
            getSubtaskData={getSubtaskData}
            modalActions={modalActions}
            maxVisibleLabels={maxVisibleLabels}
            setOpen={setOpen}
            setDeleteModalOpen={setDeleteModalOpen}
            setSelectedItem={setSelectedItem}
          />
          <Modal open={open} maxWidth={'680px'} onClose={handleCancel} isCreateTask={true}>
            <CreateTaskModalV2
              loadData={loadData}
              onCancel={handleCancel}
              context={isTemplateProject ? { source: 'template' } : { source: 'shortcut' }}
            />
          </Modal>
          <Modal open={deleteModalOpen} onClose={closeDeleteModal} maxWidth={'334px'}>
            <DeleteModal
              onClose={closeDeleteModal}
              onOk={handleDeleteSelected}
              loading={deleteLoading}
              modalTitle={`${
                (selectedItem?.subTasks?.length ?? 0) > 0
                  ? `Delete this task and ${selectedItem?.subTasks?.length} subtasks?`
                  : 'Delete Task?'
              }`}
              description={`${
                (selectedItem?.subTasks?.length ?? 0) > 0
                  ? 'This action will remove this task and all its connected subtasks.'
                  : 'Are you sure you want to delete this task?'
              }`}
            />
          </Modal>
        </Content>
        <DragOverlay>
          {activeTask && currentProject ? (
            <TaskItem
              task={activeTask}
              currentProject={currentProject}
              loadData={noop}
              setOpen={noop}
              setDeleteModelopen={noop}
              maxVisibleLabels={maxVisibleLabels}
              isTemplateProject={isTemplateProject}
              isView={isView}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      {selectedTasks?.length > 0 && (
        <BulkActionControls
          selectedTasks={selectedTasks}
          currentProject={currentProject || undefined}
          onBulkUpdateGroup={onBulkUpdateGroup}
          onBulkUpdateStatus={onBulkUpdateStatus}
          openSelectedDeleteModal={openSelectedDeleteModal}
          onRemoveSelectedTasks={onRemoveSelectedTasks}
        />
      )}
      <Modal open={selectedDeleteModalOpen} onClose={closeSelectedDeleteModal} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeSelectedDeleteModal}
          modalTitle='Delete Tasks?'
          description={bulkDeleteDescription}
          onOk={handleBulkDelete}
          loading={deleteLoading}
        />
      </Modal>
      <TaskConfirmModal ref={taskConfirmModalRef} loadData={loadData} />
    </>
  );
};

export default observer(TasksPage);
